import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@acast-tech/auth-client';
import { intercom } from '@acast-tech/product-suite-app-utils';
import { config } from '@/config';
import { fetchGraphQL } from '@/services/graphqlClient';

const INTERCOM_QUERY = `
  query ViewerIntercom {
    viewer {
      id: databaseId
      email
      firstName
      lastName
      locale
      avatarUrl(size: 150)
      createdAt
      isCreatorNetwork
    }
    viewerIntercomIdentityHash
  }
`;

interface IntercomViewer {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  locale: string | null;
  avatarUrl: string | null;
  createdAt: string | null;
  isCreatorNetwork: boolean;
}

interface IntercomQueryResponse {
  viewer: IntercomViewer;
  viewerIntercomIdentityHash: string;
}

const fetchIntercomData = (token: string): Promise<IntercomQueryResponse> =>
  fetchGraphQL<IntercomQueryResponse>({
    url: config.graphqlApiUrl,
    token,
    query: INTERCOM_QUERY,
    headers: { 'Client-Name': 'revenue/echo-nav' },
  });

/**
 * Loads the Intercom widget and boots it with the current user's profile.
 * Mirrors the Intercom integration inside AppShell but uses React Query
 * instead of suspend-react. Skips boot when impersonating.
 */
export const useIntercom = (): void => {
  const { userId, isImpersonating, getToken } = useAuth();

  const { data } = useQuery({
    queryKey: ['intercom-viewer'],
    queryFn: async () => {
      const token = await getToken();
      return fetchIntercomData(token);
    },
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    intercom.load(config.intercomAppId);
  }, []);

  useEffect(() => {
    if (userId && data && !isImpersonating) {
      intercom.boot({
        id: userId,
        email: data.viewer.email,
        firstName: data.viewer.firstName,
        lastName: data.viewer.lastName,
        hash: data.viewerIntercomIdentityHash,
        createdAt: data.viewer.createdAt,
        locale: data.viewer.locale,
        avatarUrl: data.viewer.avatarUrl,
        isCreatorNetwork: data.viewer.isCreatorNetwork,
      });
    }
  }, [userId, data, isImpersonating]);
};
